import { injectStyle } from '../css.js';
import { getProjects } from '../api.js';

// Header CSS is static, in index.html — only the (currently unused)
// secondary row's rule is still here.
export const css = `
.navbar-secondary {
  font-size: 0.85rem;
  opacity: 0.75;
}
`;
injectStyle('navbar-css', css);

const NAV_LINKS = [
  { href: '/', label: 'Home' },
  { href: '/projects', label: 'Projects' },
  { href: '/about', label: 'About' },
  { href: '/contact', label: 'Contact' },
];

// The header's markup — logo, nav links, theme toggle, contact CTA and the
// mobile-menu toggle — rendered here instead of hardcoded in index.html.
// app.js calls this synchronously, before any async work, so it still
// paints essentially immediately. All of its CSS is static, in index.html.
//
// Mobile menu = checkbox + <label>, no JS: .nav-toggle:checked opens
// .nav-menu, and the router unchecks #nav-menu-checkbox after every route
// change. The checkbox sits before the menu so the `~` selectors reach it.
export function headerShell() {
  const links = NAV_LINKS.map(
    (link) => `<li><a href="${link.href}" class="nav-link" data-link>${link.label}</a></li>`
  ).join('');

  return `
      <nav class="navbar container" aria-label="Main">
        <a href="/" class="logo" data-link aria-label="jwk.dk — home">
          <span class="logo-symbol" aria-hidden="true">&lt;/&gt;</span>
          <span>Jonas<span class="logo-dot">.</span></span>
        </a>
        <input type="checkbox" id="nav-menu-checkbox" class="nav-toggle" aria-label="Menu">
        <ul class="nav-menu" id="main-nav">${links}</ul>
        <div class="nav-actions">
          <button type="button" class="theme-button" id="theme-toggle" aria-label="Toggle light mode" aria-pressed="false">☼</button>
          <a href="/contact" class="nav-contact" data-link>Get in touch</a>
          <label for="nav-menu-checkbox" class="menu-button" aria-hidden="true">
            <span></span><span></span><span></span>
          </label>
        </div>
      </nav>
  `;
}

// The only genuinely *dynamic* part: a secondary row, fetched once and
// cached, appended after the shell above. Replace the placeholder content
// with whatever's actually worth surfacing (a project count, a "last
// updated" line, filter tags — whatever the site ends up needing).
export async function navbarSecondaryRow() {
  const projects = await getProjects();
  return `<div class="navbar-secondary">${projects.length} project${projects.length === 1 ? '' : 's'
    }</div>`;
}

// Hide-on-scroll was removed here (2026-09-14). `.header` is
// `position: sticky`, and attaching a `scroll` listener to `window`
// alongside a sticky element is a well-known Chrome forced-reflow trap:
// it has to synchronously recompute the sticky element's scroll offset
// before every single scroll callback runs, on every scroll tick — which
// is exactly what Lighthouse's "Forced reflow" audit was pointing at in
// this file (46ms total), even though nothing here ever calls
// offsetWidth/getBoundingClientRect directly. `.hidden` had no CSS rule
// defined anywhere anyway, so the listener was dead weight already.
//
// If the hide-on-scroll effect is wanted, do it with a CSS scroll-driven
// animation instead — zero JS, so this trap can't come back:
//
//   @keyframes hide-header { to { transform: translateY(-100%); } }
//   .header {
//     animation: hide-header linear both;
//     animation-timeline: scroll(root);
//     animation-range: 100px 220px;
//   }
//
// Chrome 115+; unsupported browsers just keep the header always visible,
// which is a safe fallback either way.
export function initNavbar() {
  initThemeToggle();
  markActiveLink();
  window.addEventListener('route:rendered', markActiveLink);
}

// aria-current="page" on the link for the current path — the CSS hangs the
// active color and gradient underline off that attribute.
function markActiveLink() {
  const path = location.pathname.replace(/\/+$/, '') || '/';
  document.querySelectorAll('#main-nav .nav-link').forEach((link) => {
    const href = link.getAttribute('href');
    const active = href === '/' ? path === '/' : path === href || path.startsWith(href + '/');
    if (active) link.setAttribute('aria-current', 'page');
    else link.removeAttribute('aria-current');
  });
}

// Light/dark toggle. The first paint is already right — an inline script
// at the top of <body> in index.html applies the saved choice (or the OS
// setting) before anything renders; this only syncs the button and saves
// clicks. Key: "jwk-theme".
function initThemeToggle() {
  const button = document.getElementById('theme-toggle');
  if (!button) return;

  const sync = () => {
    const light = document.body.classList.contains('light-theme');
    button.textContent = light ? '☾' : '☼';
    button.setAttribute('aria-pressed', String(light));
    button.setAttribute('aria-label', light ? 'Switch to dark mode' : 'Switch to light mode');
  };

  sync();
  button.addEventListener('click', () => {
    const light = document.body.classList.toggle('light-theme');
    try {
      localStorage.setItem('jwk-theme', light ? 'light' : 'dark');
    } catch (e) {
      // Private mode / blocked storage: the toggle still works for this visit.
    }
    sync();
  });
}
